/**
 * Design System Showcase (/design-system)
 *
 * Renders every design token (light + dark), all refactored ui/* primitives in
 * their variants/sizes, the Badge variants, and the catalog-native
 * model-display primitives fed with one sample CatalogModel per modality.
 *
 * The "Dark" section wraps its content in a `.dark` div so both themes are
 * visible on the same page without a global toggle (v1 — the persisted
 * light/dark switch is承接演进, §7).
 */

import { ReactNode, useState } from 'react'
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardContent,
  Input,
  Select,
  Textarea,
  Checkbox,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ButtonGroup,
} from '@/components/ui'
import {
  ModalityIcon,
  BillingLabel,
  ModelBadge,
  ModelCapabilitySummary,
  ModelSelect,
  ImageConfigPanel,
  ModelParameters,
} from '@/components/model-display'
import type { CatalogModel } from '@/types'

const SURFACE_TOKENS = [
  'background', 'foreground', 'card', 'card-foreground', 'popover', 'popover-foreground',
  'surface-1', 'surface-2', 'surface-3',
]
const BRAND_TOKENS = [
  'primary', 'primary-foreground', 'secondary', 'secondary-foreground',
  'muted', 'muted-foreground', 'accent', 'accent-foreground',
]
const STATE_TOKENS = [
  'success', 'success-foreground', 'warning', 'warning-foreground',
  'destructive', 'destructive-foreground', 'info', 'info-foreground',
]
const LINE_TOKENS = ['border', 'input', 'ring']

function Swatch({ token }: { token: string }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-10 h-10 rounded-md border border-border shadow-elevation-1"
        style={{ backgroundColor: `hsl(var(--${token}))` }}
      />
      <code className="text-xs text-muted-foreground">--{token}</code>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  )
}

function SwatchGrid() {
  return (
    <div className="space-y-4">
      {[
        ['Surfaces', SURFACE_TOKENS],
        ['Brand', BRAND_TOKENS],
        ['States', STATE_TOKENS],
        ['Lines', LINE_TOKENS],
      ].map(([label, tokens]) => (
        <div key={label as string}>
          <h3 className="text-sm font-medium text-muted-foreground mb-2">{label as string}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {(tokens as string[]).map((t) => <Swatch key={t} token={t} />)}
          </div>
        </div>
      ))}
    </div>
  )
}

const SAMPLE_MODELS: CatalogModel[] = [
  {
    id: 'claude-sonnet-4',
    source: 'stable',
    display_name: 'Claude Sonnet 4',
    provider: 'anthropic',
    routing_tier: 'official',
    quality_tier_display: 'Flagship',
    quality_tier_icon: '⭐',
    billing: {
      billing_mode: 'per_token',
      input_per_million: 3,
      output_per_million: 15,
      cache_read_per_million: 0.3,
      cache_write_per_million: 3.75,
      currency: 'USD',
    },
    capability: {
      modality: 'text',
      max_input_tokens: 200000,
      max_output_tokens: 64000,
      supports_vision: true,
      supports_function_calling: true,
      supports_reasoning: true,
      supports_json_mode: true,
      supported_params: [],
    },
  },
  {
    id: 'gemini-2.5-flash-image',
    source: 'stable',
    display_name: 'Gemini 2.5 Flash Image',
    provider: 'google',
    routing_tier: 'stable',
    quality_tier_display: 'Standard',
    billing: { billing_mode: 'per_image', cost_per_image: 0.039, currency: 'USD' },
    capability: {
      modality: 'image',
      operations: ['generate', 'edit', 'fuse'],
      sizing: { mode: 'discrete_size', sizes: ['1024x1024'] },
      max_images_edit: 2,
      max_images_fuse: 2,
      quality_options: ['standard', 'hd'],
      output_formats: ['png', 'jpeg'],
      supports_transparency: true,
      max_images_out: 4,
      default_size: '1024x1024',
    },
  },
  {
    id: 'tts-cosyvoice',
    source: 'stable',
    display_name: 'CosyVoice TTS',
    provider: 'volc',
    routing_tier: 'stable',
    billing: { billing_mode: 'per_character', cost_per_1k_chars: 0.12, currency: 'CNY' },
    capability: {
      modality: 'tts',
      voices: [
        { id: 'v1', name: 'Aria' },
        { id: 'v2', name: 'Leo' },
        { id: 'v3', name: 'Mei' },
      ],
      audio_formats: ['mp3', 'wav'],
      supports_streaming: true,
    },
  },
  {
    id: 'whisper-large-v3',
    source: 'stable',
    display_name: 'Whisper Large v3',
    provider: 'openai',
    routing_tier: 'dev',
    billing: { billing_mode: 'per_second', cost_per_minute: 0.006, currency: 'USD' },
    capability: {
      modality: 'stt',
      input_formats: ['wav', 'mp3', 'm4a'],
      languages: ['en', 'zh', 'ja', 'es'],
      supports_streaming: false,
    },
  },
  {
    id: 'bge-m3',
    source: 'stable',
    display_name: 'BGE-M3',
    provider: 'baai',
    routing_tier: 'stable',
    billing: { billing_mode: 'free' },
    capability: { modality: 'embedding', dimensions: 1024, max_input_tokens: 8192 },
  },
  {
    id: 'bge-reranker-v2',
    source: 'stable',
    display_name: 'BGE Reranker v2',
    provider: 'baai',
    routing_tier: 'bltcy',
    billing: { billing_mode: 'free' },
    capability: { modality: 'rerank', max_documents: 100 },
  },
]

/**
 * Controlled showcase for ModelSelect — exercises all three variants.
 * Uses a real QueryClientProvider so the component fetches the catalog.
 */
function ModelSelectShowcase() {
  const [selectedText, setSelectedText] = useState('')
  const [selectedTts, setSelectedTts] = useState('')
  const [selectedModal, setSelectedModal] = useState('')
  const [selectedImageGen, setSelectedImageGen] = useState('')

  return (
    <Section title="ModelSelect">
      <p className="text-sm text-muted-foreground">
        Catalog-native unified selector — reads CatalogModel directly (no flattening).
        Composes ModelBadge + BillingLabel + ModelCapabilitySummary.
      </p>
      <div className="grid gap-6 md:grid-cols-3">
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">inline — text models</h3>
          <ModelSelect
            value={selectedText}
            onChange={setSelectedText}
            modality="text"
            variant="inline"
          />
          {selectedText && (
            <p className="text-xs text-muted-foreground">Selected: {selectedText}</p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">compact — tts models</h3>
          <ModelSelect
            value={selectedTts}
            onChange={setSelectedTts}
            modality="tts"
            variant="compact"
          />
          {selectedTts && (
            <p className="text-xs text-muted-foreground">Selected: {selectedTts}</p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">modal — all models</h3>
          <ModelSelect
            value={selectedModal}
            onChange={setSelectedModal}
            variant="modal"
          />
          {selectedModal && (
            <p className="text-xs text-muted-foreground">Selected: {selectedModal}</p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">
            inline — image / operationFilter=&quot;generate&quot;
          </h3>
          <ModelSelect
            value={selectedImageGen}
            onChange={setSelectedImageGen}
            modality="image"
            operationFilter="generate"
            variant="inline"
          />
          {selectedImageGen && (
            <p className="text-xs text-muted-foreground">Selected: {selectedImageGen}</p>
          )}
        </div>
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// ImageConfigPanel showcase (static sample model, controlled state)
// ---------------------------------------------------------------------------

const SHOWCASE_IMAGE_MODEL: CatalogModel = {
  id: 'gpt-image-1',
  source: 'stable',
  display_name: 'GPT Image 1',
  provider: 'openai',
  routing_tier: 'official',
  billing: { billing_mode: 'per_image', cost_per_image: 0.04, currency: 'USD' },
  capability: {
    modality: 'image',
    operations: ['generate', 'edit'],
    sizing: { mode: 'discrete_size', sizes: ['1024x1024', '1536x1024', '1024x1536'] },
    quality_options: ['auto', 'high', 'medium', 'low'],
    output_formats: ['png', 'jpeg', 'webp'],
    supports_transparency: true,
    max_images_out: 1,
    max_images_edit: 10,
    max_images_fuse: 0,
    default_size: '1024x1024',
  },
}

const SHOWCASE_ASPECT_MODEL: CatalogModel = {
  id: 'gemini-imagen-3',
  source: 'stable',
  display_name: 'Imagen 3',
  provider: 'google',
  routing_tier: 'stable',
  billing: { billing_mode: 'per_image', cost_per_image: 0.04, currency: 'USD' },
  capability: {
    modality: 'image',
    operations: ['generate'],
    sizing: { mode: 'aspect_ratio', aspect_ratios: ['1:1', '3:4', '4:3', '9:16', '16:9'] },
    quality_options: [],
    output_formats: ['png', 'jpeg'],
    supports_transparency: false,
    max_images_out: 4,
    max_images_edit: 0,
    max_images_fuse: 0,
  },
}

function ImageConfigPanelShowcase() {
  const [discreteValue, setDiscreteValue] = useState<{
    size?: string | null
    quality?: string | null
  }>({})
  const [aspectValue, setAspectValue] = useState<{
    aspect_ratio?: string | null
  }>({})

  return (
    <Section title="ImageConfigPanel (B2)">
      <p className="text-sm text-muted-foreground">
        Catalog-native image config panel — reads ImageCapability directly from CatalogModel.
        Sizing mode dispatches to Size or Aspect Ratio dropdown; quality shown only when non-empty.
      </p>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">
            discrete_size + quality (GPT Image 1)
          </h3>
          <ImageConfigPanel
            model={SHOWCASE_IMAGE_MODEL}
            value={discreteValue}
            onChange={(patch) => setDiscreteValue((prev) => ({ ...prev, ...patch }))}
          />
          {(discreteValue.size || discreteValue.quality) && (
            <p className="text-xs text-muted-foreground">
              size={discreteValue.size ?? '—'} quality={discreteValue.quality ?? '—'}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">
            aspect_ratio mode (Imagen 3)
          </h3>
          <ImageConfigPanel
            model={SHOWCASE_ASPECT_MODEL}
            value={aspectValue}
            onChange={(patch) => setAspectValue((prev) => ({ ...prev, ...patch }))}
          />
          {aspectValue.aspect_ratio && (
            <p className="text-xs text-muted-foreground">
              aspect_ratio={aspectValue.aspect_ratio}
            </p>
          )}
        </div>
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// ModelParameters showcase (static sample model, controlled state)
// ---------------------------------------------------------------------------

const SHOWCASE_TEXT_MODEL_WITH_PARAMS: CatalogModel = {
  id: 'claude-sonnet-4',
  source: 'litellm',
  display_name: 'Claude Sonnet 4',
  provider: 'anthropic',
  routing_tier: 'official',
  billing: {
    billing_mode: 'per_token',
    input_per_million: 3,
    output_per_million: 15,
    cache_read_per_million: 0.3,
    cache_write_per_million: 3.75,
    currency: 'USD',
  },
  capability: {
    modality: 'text',
    max_input_tokens: 200000,
    max_output_tokens: 64000,
    supports_vision: true,
    supports_function_calling: true,
    supports_reasoning: true,
    supports_json_mode: true,
    supported_params: [
      { name: 'temperature', type: 'float', min: 0, max: 2, default: 1.0, supported: true, conflicts_with: ['top_p'] },
      { name: 'top_p', type: 'float', min: 0, max: 1, default: 0.9, supported: true, conflicts_with: ['temperature'] },
      { name: 'max_tokens', type: 'int', min: 1, max: null, default: 4096, supported: true, conflicts_with: [] },
      { name: 'stream', type: 'bool', default: false, supported: true, conflicts_with: [] },
    ],
  },
}

const SHOWCASE_REASONING_MODEL: CatalogModel = {
  id: 'o3-mini',
  source: 'litellm',
  display_name: 'o3-mini (reasoning — no temperature)',
  provider: 'openai',
  billing: {
    billing_mode: 'per_token',
    input_per_million: 1.1,
    output_per_million: 4.4,
    cache_read_per_million: null,
    cache_write_per_million: null,
    currency: 'USD',
  },
  capability: {
    modality: 'text',
    max_input_tokens: 128000,
    max_output_tokens: 65536,
    supports_vision: false,
    supports_function_calling: true,
    supports_reasoning: true,
    supports_json_mode: true,
    supported_params: [
      { name: 'max_tokens', type: 'int', min: 1, max: 65536, default: 4096, supported: true, conflicts_with: [] },
    ],
  },
}

function ModelParametersShowcase() {
  const [textValues, setTextValues] = useState<Record<string, number | boolean | string | null>>({
    temperature: 1.0,
    top_p: 0.9,
    max_tokens: 4096,
    stream: false,
  })
  const [reasoningValues, setReasoningValues] = useState<Record<string, number | boolean | string | null>>({
    max_tokens: 4096,
  })

  function handleTextChange(name: string, value: number | boolean | string | null) {
    setTextValues((prev) => ({ ...prev, [name]: value }))
  }

  function handleReasoningChange(name: string, value: number | boolean | string | null) {
    setReasoningValues((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <Section title="ModelParameters">
      <p className="text-sm text-muted-foreground">
        Catalog-native parameter panel — renders only the params each model actually supports
        (per <code>TextCapability.supported_params</code>). Reasoning/o-series models omit
        temperature/top_p automatically.
      </p>
      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">
            Claude Sonnet 4 (float + bool + int)
          </h3>
          <ModelParameters
            model={SHOWCASE_TEXT_MODEL_WITH_PARAMS}
            values={textValues}
            onChange={handleTextChange}
          />
          <pre className="text-xs text-muted-foreground bg-muted p-2 rounded">
            {JSON.stringify(textValues, null, 2)}
          </pre>
        </div>
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">
            o3-mini (reasoning — only max_tokens)
          </h3>
          <ModelParameters
            model={SHOWCASE_REASONING_MODEL}
            values={reasoningValues}
            onChange={handleReasoningChange}
          />
          <pre className="text-xs text-muted-foreground bg-muted p-2 rounded">
            {JSON.stringify(reasoningValues, null, 2)}
          </pre>
        </div>
      </div>
    </Section>
  )
}

function PrimitivesShowcase() {
  return (
    <div className="space-y-6">
      <Section title="Button">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="ghost">Ghost</Button>
          <Button loading>Loading</Button>
          <Button disabled>Disabled</Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
        </div>
      </Section>

      <Section title="Badge">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="default">default</Badge>
          <Badge variant="secondary">secondary</Badge>
          <Badge variant="success">success</Badge>
          <Badge variant="warning">warning</Badge>
          <Badge variant="destructive">destructive</Badge>
          <Badge variant="info">info</Badge>
          <Badge variant="outline">outline</Badge>
          <Badge variant="info" size="md">md size</Badge>
        </div>
      </Section>

      <Section title="Form inputs">
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
          <Input label="Input" placeholder="Type here" helperText="Helper text" />
          <Input label="Input (error)" placeholder="Invalid" error="Required field" />
          <Select
            label="Select"
            options={[
              { value: 'a', label: 'Option A' },
              { value: 'b', label: 'Option B' },
            ]}
          />
          <Textarea label="Textarea" placeholder="Multi-line" rows={3} />
          <Checkbox label="Checkbox" defaultChecked />
          <div className="flex items-center gap-4">
            <Switch checked onChange={() => {}} ariaLabel="on" />
            <Switch checked={false} onChange={() => {}} ariaLabel="off" />
            <Switch checked onChange={() => {}} size="sm" onLabel="On" offLabel="Off" />
          </div>
        </div>
      </Section>

      <Section title="ButtonGroup">
        <ButtonGroup
          value="b"
          onChange={() => {}}
          options={[
            { value: 'a', label: 'Day' },
            { value: 'b', label: 'Week' },
            { value: 'c', label: 'Month' },
          ]}
        />
      </Section>

      <Section title="Tabs">
        <Tabs defaultValue="one">
          <TabsList>
            <TabsTrigger value="one">One</TabsTrigger>
            <TabsTrigger value="two">Two</TabsTrigger>
            <TabsTrigger value="three">Three</TabsTrigger>
          </TabsList>
          <TabsContent value="one" className="pt-3 text-sm text-muted-foreground">Tab one content</TabsContent>
          <TabsContent value="two" className="pt-3 text-sm text-muted-foreground">Tab two content</TabsContent>
          <TabsContent value="three" className="pt-3 text-sm text-muted-foreground">Tab three content</TabsContent>
        </Tabs>
      </Section>

      <Section title="Card">
        <Card className="max-w-md">
          <CardHeader title="Card title" description="Card description with muted text." />
          <CardContent className="text-sm text-foreground">
            Card body content sits on the <code>bg-card</code> surface.
          </CardContent>
        </Card>
      </Section>

      <Section title="Model-display primitives">
        <div className="grid gap-3 md:grid-cols-2">
          {SAMPLE_MODELS.map((m) => (
            <Card key={m.id} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{m.display_name}</span>
                <ModalityIcon modality={m.capability.modality} size={18} />
              </div>
              <ModelBadge model={m} />
              <ModelCapabilitySummary capability={m.capability} />
              <BillingLabel billing={m.billing} />
            </Card>
          ))}
        </div>
      </Section>

      <ModelSelectShowcase />
      <ImageConfigPanelShowcase />
      <ModelParametersShowcase />
    </div>
  )
}

export function DesignSystemPage() {
  return (
    <div className="min-h-screen bg-background text-foreground p-8 space-y-12">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold">Design System v1</h1>
        <p className="text-muted-foreground">
          Token-driven, dark-ready primitives + catalog-native model-display components.
          Refs dora-agi/dora#958.
        </p>
      </header>

      <Section title="Tokens">
        <SwatchGrid />
      </Section>

      <Section title="Primitives — Light">
        <PrimitivesShowcase />
      </Section>

      <div className="dark rounded-xl border border-border bg-background text-foreground p-8 space-y-3">
        <h2 className="text-xl font-semibold">Primitives + Tokens — Dark</h2>
        <p className="text-sm text-muted-foreground">
          This block sets the <code>.dark</code> class; tokens flip automatically.
        </p>
        <SwatchGrid />
        <PrimitivesShowcase />
      </div>
    </div>
  )
}
